import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Eye, FileText, Clock, Calendar, Image, Download, Linkedin, Sparkles, Copy, X, Loader2 } from 'lucide-react';
import axios from 'axios';

// Conditionally import ReactQuill with error boundary
let ReactQuill = null;
try {
  ReactQuill = require('react-quill').default;
  require('react-quill/dist/quill.snow.css');
} catch (e) {
  console.warn('ReactQuill not available, falling back to textarea');
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogManagement = ({ auth }) => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, scheduled: 0, total_views: 0 });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // LinkedIn Content Generator state
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [linkedInPost, setLinkedInPost] = useState(null);
  const [generatingLinkedIn, setGeneratingLinkedIn] = useState(false);
  const [linkedInContent, setLinkedInContent] = useState('');
  
  // Image Preview state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState({ url: '', title: '' });
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    content_type: 'richtext',
    category: 'Leadership',
    tags: [],
    featured_image: '',
    read_time: 5,
    status: 'draft',
    scheduled_date: ''
  });
  
  const [tagInput, setTagInput] = useState('');

  const categories = ['HR', 'Leadership', 'Innovation', 'Trends', 'Talent Acquisition'];

  useEffect(() => {
    loadPosts();
    loadStats();
    loadSchedulerStatus();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/admin/posts`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/admin/stats`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/admin/scheduler-status`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error loading scheduler status:', error);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.content) {
        toast({
          title: "Error",
          description: "Title and content are required",
          variant: "destructive"
        });
        return;
      }

      if (editingPost) {
        await axios.put(
          `${BACKEND_URL}/api/blog/admin/posts/${editingPost.id}`,
          formData,
          { headers: { 'Authorization': `Basic ${auth}` } }
        );
        toast({ title: "Success", description: "Post updated successfully" });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/blog/admin/posts`,
          formData,
          { headers: { 'Authorization': `Basic ${auth}` } }
        );
        toast({ title: "Success", description: "Post created successfully" });
      }

      setShowEditor(false);
      setEditingPost(null);
      resetForm();
      loadPosts();
      loadStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save post",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    // Fix: Handle scheduled_date with timezone properly
    let scheduledDate = '';
    if (post.scheduled_date) {
      try {
        // Parse the ISO date and convert to local datetime-local format
        const date = new Date(post.scheduled_date);
        // Add IST offset (5:30 hours) to get local time
        const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
        scheduledDate = istDate.toISOString().slice(0, 16);
      } catch (e) {
        scheduledDate = post.scheduled_date.slice(0, 16);
      }
    }
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      content_type: post.content_type || 'richtext',
      category: post.category,
      tags: post.tags || [],
      featured_image: post.featured_image || '',
      read_time: post.read_time || 5,
      status: post.status,
      scheduled_date: scheduledDate
    });
    setShowEditor(true);
  };

  // Image Preview Handler
  const handleImagePreview = (url, title) => {
    setPreviewImage({ url, title });
    setShowImagePreview(true);
  };

  // Download Image Handler
  const handleDownloadImage = async (url, title) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast({ title: "Success", description: "Image downloaded!" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download image", variant: "destructive" });
    }
  };

  // LinkedIn Content Generator
  const generateLinkedInContent = async (post) => {
    setLinkedInPost(post);
    setShowLinkedInModal(true);
    setGeneratingLinkedIn(true);
    setLinkedInContent('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/blog/admin/generate-linkedin`,
        {
          title: post.title,
          excerpt: post.excerpt,
          content: post.content?.substring(0, 2000),
          category: post.category,
          tags: post.tags,
          blog_url: `${window.location.origin}/blog/${post.slug}`
        },
        { headers: { 'Authorization': `Basic ${auth}` } }
      );
      setLinkedInContent(response.data.content);
    } catch (error) {
      // Fallback: Generate basic content without AI
      const hashtags = (post.tags || []).slice(0, 5).map(t => `#${t.replace(/\s+/g, '')}`).join(' ');
      const fallbackContent = `🚀 New Article: ${post.title}

${post.excerpt}

📖 Read the full article: ${window.location.origin}/blog/${post.slug}

${hashtags} #Leadership #ProfessionalDevelopment #CareerGrowth`;
      setLinkedInContent(fallbackContent);
    } finally {
      setGeneratingLinkedIn(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Content copied to clipboard" });
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/blog/admin/posts/${postId}`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      toast({ title: "Success", description: "Post deleted successfully" });
      loadPosts();
      loadStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      content_type: 'richtext',
      category: 'Leadership',
      tags: [],
      featured_image: '',
      read_time: 5,
      status: 'draft',
      scheduled_date: ''
    });
    setTagInput('');
  };

  const handleAddTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 20;

  const filteredPosts = filterStatus === 'all' 
    ? posts 
    : posts.filter(p => p.status === filterStatus);

  // Pagination logic
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  if (showEditor) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-xl md:text-2xl font-bold">
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => {
              setShowEditor(false);
              setEditingPost(null);
              resetForm();
            }} className="flex-1 sm:flex-none">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700 flex-1 sm:flex-none">
              Save Post
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-4 md:p-6 space-y-4 md:space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter post title"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Excerpt *</label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief summary (will appear in listings)"
                rows={3}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Content *</label>
              {/* Use simple textarea for React 19 compatibility */}
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your content here..."
                rows={15}
                className="font-normal text-sm min-h-[300px]"
              />
              <p className="text-xs text-slate-500 mt-2">
                Supports HTML formatting. You can add headings, links, and styling using HTML tags.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="mb-2 block text-sm font-medium">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Read Time (minutes)</label>
                <Input
                  type="number"
                  value={formData.read_time}
                  onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) })}
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Tags</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tags"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  className="flex-1"
                />
                <Button onClick={handleAddTag} size="sm">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="cursor-pointer text-xs" onClick={() => handleRemoveTag(tag)}>
                    {tag} ×
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Featured Image URL</label>
              <Input
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Status</label>
              <div className="flex flex-wrap gap-3 md:gap-4 mb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    value="draft"
                    checked={formData.status === 'draft'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value, scheduled_date: '' })}
                  />
                  Draft
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    value="scheduled"
                    checked={formData.status === 'scheduled'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  />
                  Scheduled
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    value="published"
                    checked={formData.status === 'published'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value, scheduled_date: '' })}
                  />
                  Published
                </label>
              </div>
              
              {formData.status === 'scheduled' && (
                <div className="mt-4 p-3 md:p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <label className="mb-2 block text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Schedule Publication Date & Time (IST)
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="max-w-full sm:max-w-xs"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    The article will be automatically published at the scheduled time.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-bold">Blog Management</h2>
        <Button onClick={() => setShowEditor(true)} className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-3 md:gap-6 grid-cols-2 md:grid-cols-5">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-3 md:p-6">
            <p className="text-xs md:text-sm text-slate-600">Total</p>
            <p className="text-xl md:text-3xl font-bold text-blue-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-3 md:p-6">
            <p className="text-xs md:text-sm text-slate-600">Published</p>
            <p className="text-xl md:text-3xl font-bold text-green-900">{stats.published}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-3 md:p-6">
            <p className="text-xs md:text-sm text-slate-600 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Scheduled
            </p>
            <p className="text-xl md:text-3xl font-bold text-amber-900">{stats.scheduled || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="p-3 md:p-6">
            <p className="text-xs md:text-sm text-slate-600">Drafts</p>
            <p className="text-xl md:text-3xl font-bold text-slate-900">{stats.drafts}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 col-span-2 md:col-span-1">
          <CardContent className="p-3 md:p-6">
            <p className="text-xs md:text-sm text-slate-600">Views</p>
            <p className="text-xl md:text-3xl font-bold text-purple-900">{stats.total_views}</p>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler Status Banner */}
      {schedulerStatus && schedulerStatus.total_scheduled > 0 && (
        <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 md:gap-3">
                <Clock className="h-5 w-5 md:h-6 md:w-6 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm md:text-base">Auto-Scheduler Active</p>
                  <p className="text-xs md:text-sm opacity-90">
                    {schedulerStatus.total_scheduled} articles scheduled for automatic publication at 4 AM IST
                  </p>
                </div>
              </div>
              {schedulerStatus.upcoming_posts && schedulerStatus.upcoming_posts[0] && (
                <div className="text-left sm:text-right text-xs md:text-sm mt-2 sm:mt-0">
                  <p className="opacity-90 truncate max-w-[200px] md:max-w-none">Next: {schedulerStatus.upcoming_posts[0].title.slice(0, 30)}...</p>
                  <p className="font-medium">
                    {new Date(schedulerStatus.upcoming_posts[0].scheduled_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={filterStatus === 'all' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('all')}
          size="sm"
          className="text-xs md:text-sm"
        >
          All ({posts.length})
        </Button>
        <Button
          variant={filterStatus === 'published' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('published')}
          size="sm"
          className="text-xs md:text-sm"
        >
          Published ({posts.filter(p => p.status === 'published').length})
        </Button>
        <Button
          variant={filterStatus === 'scheduled' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('scheduled')}
          size="sm"
          className={`text-xs md:text-sm ${filterStatus === 'scheduled' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
        >
          <Clock className="h-3 w-3 mr-1" />
          Scheduled ({posts.filter(p => p.status === 'scheduled').length})
        </Button>
        <Button
          variant={filterStatus === 'draft' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('draft')}
          size="sm"
          className="text-xs md:text-sm"
        >
          Drafts ({posts.filter(p => p.status === 'draft').length})
        </Button>
      </div>

      {/* Pagination Info */}
      {filteredPosts.length > postsPerPage && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs md:text-sm text-slate-500">
          <span>
            Showing {startIndex + 1}-{Math.min(startIndex + postsPerPage, filteredPosts.length)} of {filteredPosts.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="text-xs"
            >
              Prev
            </Button>
            <span className="px-2 py-1 bg-slate-100 rounded text-xs">
              {currentPage}/{totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="text-xs"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Posts List */}
      <div className="space-y-3 md:space-y-4">
        {paginatedPosts.map((post) => (
          <Card key={post.id} className={post.status === 'scheduled' ? 'border-amber-200 bg-amber-50/30' : ''}>
            <CardContent className="p-3 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-semibold text-sm md:text-lg line-clamp-2">{post.title}</h3>
                    <Badge 
                      variant={post.status === 'published' ? 'default' : post.status === 'scheduled' ? 'outline' : 'secondary'}
                      className={`text-xs ${post.status === 'scheduled' ? 'bg-amber-100 text-amber-800 border-amber-300' : ''}`}
                    >
                      {post.status === 'scheduled' && <Clock className="h-3 w-3 mr-1" />}
                      {post.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs">{post.category}</Badge>
                  </div>
                  <p className="text-xs md:text-sm text-slate-600 mb-2 md:mb-3 line-clamp-2">{post.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-2 md:gap-6 text-xs md:text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3 md:h-4 md:w-4" />
                      {post.views}
                    </span>
                    {post.status === 'scheduled' && post.scheduled_date && (
                      <span className="flex items-center gap-1 text-amber-700 font-medium">
                        <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                        {new Date(post.scheduled_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short'
                        })}
                      </span>
                    )}
                    <span className="hidden sm:inline">{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex gap-1 md:gap-2 flex-shrink-0 flex-wrap">
                  {/* Image Preview */}
                  {post.featured_image && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleImagePreview(post.featured_image, post.title)} 
                      className="p-2"
                      title="Preview Image"
                    >
                      <Image className="h-4 w-4" />
                    </Button>
                  )}
                  {/* LinkedIn Generator */}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => generateLinkedInContent(post)} 
                    className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Generate LinkedIn Post"
                  >
                    <Linkedin className="h-4 w-4" />
                  </Button>
                  {/* View Post */}
                  <Button size="sm" variant="outline" onClick={() => window.open(`/blog/${post.slug}`, '_blank')} className="p-2" title="View Post">
                    <FileText className="h-4 w-4" />
                  </Button>
                  {/* Edit Post */}
                  <Button size="sm" variant="outline" onClick={() => handleEdit(post)} className="p-2" title="Edit Post">
                    <Edit className="h-4 w-4" />
                  </Button>
                  {/* Delete Post */}
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(post.id)} className="p-2" title="Delete Post">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {paginatedPosts.length === 0 && (
          <div className="text-center py-8 md:py-12 text-slate-500 text-sm">
            No posts found. Click "New Post" to create one.
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {showImagePreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowImagePreview(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 md:p-4 border-b">
              <h3 className="font-semibold text-sm md:text-base truncate pr-4">{previewImage.title}</h3>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => handleDownloadImage(previewImage.url, previewImage.title)}>
                  <Download className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Download</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowImagePreview(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center bg-slate-100" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              <img 
                src={previewImage.url} 
                alt={previewImage.title} 
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Content Generator Modal */}
      {showLinkedInModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowLinkedInModal(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 md:p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center gap-2">
                <Linkedin className="h-5 w-5" />
                <h3 className="font-semibold text-sm md:text-base">LinkedIn Post Generator</h3>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setShowLinkedInModal(false)} className="text-white hover:bg-white/20">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 md:p-6">
              {linkedInPost && (
                <div className="mb-4 text-sm text-slate-600">
                  <strong>Article:</strong> {linkedInPost.title}
                </div>
              )}
              {generatingLinkedIn ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-3" />
                  <p className="text-slate-600">Generating LinkedIn content...</p>
                </div>
              ) : (
                <>
                  <Textarea
                    value={linkedInContent}
                    onChange={(e) => setLinkedInContent(e.target.value)}
                    placeholder="LinkedIn post content will appear here..."
                    rows={10}
                    className="mb-4 font-normal"
                  />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      onClick={() => copyToClipboard(linkedInContent)} 
                      className="bg-blue-600 hover:bg-blue-700 flex-1"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy to Clipboard
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => generateLinkedInContent(linkedInPost)}
                      className="flex-1"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-4 text-center">
                    Copy the content above and paste it into your LinkedIn post composer
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Pagination */}
      {filteredPosts.length > postsPerPage && (
        <div className="flex flex-wrap items-center justify-center gap-1 md:gap-2 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="text-xs hidden sm:inline-flex"
          >
            First
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="text-xs"
          >
            Prev
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 3) {
                pageNum = i + 1;
              } else if (currentPage <= 2) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 1) {
                pageNum = totalPages - 2 + i;
              } else {
                pageNum = currentPage - 1 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className="w-8 md:w-10 text-xs"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="text-xs"
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="text-xs hidden sm:inline-flex"
          >
            Last
          </Button>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
